import React, { useState } from 'react'
import type { StatusButtonsProps } from './StatusButtons.types'
import { StatusTypes } from 'pages/Applications/applicationStatuses'
import { type UpdateStatusData } from 'pages/Applications/Applications.types'
import Button from '@mui/material/Button'
import { useTranslation } from 'react-i18next'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { RejectButton } from './features/reject-button'
import { useNavigate } from 'react-router-dom'
import { Can } from 'config/ability'

const StatusButtons = ({
  status,
  id,
  updateHandler,
  flow,
  prize,
  receiptValidation,
  size,
  langId,
}: StatusButtonsProps): JSX.Element => {
  const [newData, setNewData] = useState<null | UpdateStatusData>(null)
  const navigate = useNavigate()
  const { t } = useTranslation()

  const clearConfirmationData = (): void => {
    setNewData(null)
  }

  const renderButton = (
    title: string,
    color: React.ComponentProps<typeof Button>['color'],
    data: UpdateStatusData,
    withConfirmation?: boolean
  ): JSX.Element => (
    <Button
      size={size}
      variant={data?.newStatusId === StatusTypes.Accepted ? 'contained' : 'text'}
      color={color}
      onClick={() => {
        if (withConfirmation !== undefined && withConfirmation) {
          setNewData(data)
        } else {
          updateHandler(data)
        }
      }}
    >
      {title}
    </Button>
  )

  const isAccepted = status === StatusTypes.Accepted
  const isFraud = status === StatusTypes.Fraud
  const isRejected = status === StatusTypes.Rejected
  const isReciptValidation = status === StatusTypes.ReciptValidation
  const isGwp = flow === 'gwp'
  const isDigital = prize === 'digital'
  const isAuto = receiptValidation === 'auto'
  const isChangeDisabled = isGwp && isDigital && (isAuto || (!isAuto && isAccepted))

  if (isChangeDisabled) {
    return <>{null}</>
  }

  const isManualReciptValidation = !isAuto && isReciptValidation
  const isManualRejected = !isAuto && isRejected
  const isRejectBtnEnabled = isManualReciptValidation
  const isFraudBtnEnabled = isAccepted || isManualReciptValidation || isManualRejected
  const isAcceptBtnEnabled = isFraud || isManualReciptValidation || isManualRejected

  return (
    <>
      <Can I="delete" an="Applications">
        {renderButton(t('common.delete'), 'error', { id, newStatusId: StatusTypes.Deleted }, true)}
      </Can>
      {isFraudBtnEnabled
        ? renderButton(t('application.fraud'), 'warning', { id, newStatusId: StatusTypes.Fraud }, true)
        : null}
      <Button
        variant="text"
        color="secondary"
        onClick={() => {
          navigate(`/applications/edit/${id}`)
        }}
      >
        {t('common.edit')}
      </Button>
      {isRejectBtnEnabled ? <RejectButton langId={langId} id={id} /> : null}
      {isAcceptBtnEnabled
        ? renderButton(t('application.accept'), 'success', { id, newStatusId: StatusTypes.Accepted })
        : null}
      {newData !== null ? (
        <ConfirmDialog
          open={newData !== null}
          onAgree={() => {
            updateHandler(newData)
            clearConfirmationData()
          }}
          onDisagree={clearConfirmationData}
          question={t('dialogs.areYouSure')}
        />
      ) : null}
    </>
  )
}

export default StatusButtons
